import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChefHat, Loader2, Lock, AlertCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/auth/invite/${token}`);
        setInviteInfo(res.data.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invitation invalide ou expirée');
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  const handleActivate = async () => {
    if (password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    try {
      setActivating(true);
      await axios.post(`${API}/auth/invite/${token}/activate`, null, {
        params: { password }
      });
      toast.success('Compte créé ! Bienvenue dans l\'équipe !');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-neon-pink animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">Invitation invalide</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <Button onClick={() => navigate('/login')} className="bg-neon-pink hover:bg-neon-pink/90 rounded-full">
            Aller à la page de connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="invite-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <img src="/logo.png" alt="Chef's Connect" className="w-16 h-16 rounded-2xl" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">Bienvenue !</h1>
          <p className="text-gray-500">Créez votre mot de passe pour accéder à Chef's Connect</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <div className="bg-gray-100 border border-gray-100 rounded-xl p-4 mb-6">
            <p className="text-xs text-gray-400 mb-1">Votre nom</p>
            <p className="font-heading font-bold text-gray-900 text-lg">{inviteInfo?.member_name}</p>
            {inviteInfo?.station && (
              <p className="text-sm text-neon-pink mt-1">{inviteInfo.station.replace('Poste ', '')}</p>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-gray-700">Mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 caractères"
                  className="pl-10 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400"
                  data-testid="invite-password-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-700">Confirmer le mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Retapez votre mot de passe"
                  className="pl-10 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400"
                  data-testid="invite-confirm-password-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleActivate()}
                />
              </div>
            </div>
          </div>

          <Button
            onClick={handleActivate}
            disabled={activating || password.length < 8 || password !== confirmPassword}
            className="w-full mt-6 bg-neon-pink hover:bg-neon-pink/90 rounded-full h-12 text-lg shadow-neon-pink"
            data-testid="activate-account-btn"
          >
            {activating ? (
              <Loader2 className="animate-spin mr-2" size={20} />
            ) : (
              <Check className="mr-2" size={20} />
            )}
            Créer mon compte
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InvitePage;
